/**
 * As regras do painel Estoque Full. Sem React e sem MUI de propósito: dá para conferir a conta
 * sem abrir o navegador, e a revisão do dev fica sobre a regra, não sobre `<div>`.
 *
 * As definições abaixo são as mesmas do Artifact de 03/09/2026, que a Mariana validou. Elas
 * também estão implementadas no motor em Python (`montar_dados.py`); aqui só se FILTRA,
 * ORDENA e FORMATA o que o motor já calculou — nenhuma conta de negócio é refeita na tela,
 * senão as duas implementações divergem com o tempo.
 */
import type {
  CandidatoFull,
  EstoqueFullData,
  PerdaFull,
  ResumoFull,
  SaiuDoFull,
  Situacao,
  SkuFull,
} from './types'

export const TODAS_AS_CONTAS = 'all'

export const SITUACAO_ROTULO: Record<Situacao, string> = {
  ruptura: 'Ruptura',
  critico: 'Crítico',
  repor: 'Repor',
  ok: 'Saudável',
  excesso: 'Excesso',
  encalhado: 'Sem giro',
  zerado_sem_giro: 'Zerado',
}

/** Tom do MUI por situação. Sempre pelo tema — cor escrita na mão quebra no modo escuro. */
export const SITUACAO_TOM: Record<Situacao, 'error' | 'warning' | 'success' | 'secondary' | 'default'> = {
  ruptura: 'error',
  critico: 'error',
  repor: 'warning',
  ok: 'success',
  excesso: 'secondary',
  encalhado: 'secondary',
  zerado_sem_giro: 'default',
}

/** Quem entra na lista "Repor agora" e quem entra na "Encalhado". */
export const SITUACOES_REPOR: Situacao[] = ['ruptura', 'critico', 'repor']
export const SITUACOES_PARADO: Situacao[] = ['encalhado', 'excesso', 'zerado_sem_giro']

// ---------------------------------------------------------------- filtros

function texto(...partes: Array<string | null | undefined>) {
  return partes.filter(Boolean).join(' ').toLowerCase()
}

export function filtrarSkus(skus: SkuFull[], conta: string, busca: string): SkuFull[] {
  const termo = busca.trim().toLowerCase()
  return skus.filter((s) => {
    if (conta !== TODAS_AS_CONTAS && s.conta !== conta) return false
    if (!termo) return true
    return texto(s.titulo, s.inv, s.anuncios.join(' '), s.variacoes.join(' ')).includes(termo)
  })
}

export function filtrarCandidatos(
  candidatos: CandidatoFull[],
  conta: string,
  busca: string,
): CandidatoFull[] {
  const termo = busca.trim().toLowerCase()
  return candidatos.filter((c) => {
    if (conta !== TODAS_AS_CONTAS && c.conta !== conta) return false
    if (!termo) return true
    return texto(c.titulo, c.mlb).includes(termo)
  })
}

export function filtrarSaiu(lista: SaiuDoFull[], conta: string, busca: string): SaiuDoFull[] {
  const termo = busca.trim().toLowerCase()
  return lista.filter((s) => {
    if (conta !== TODAS_AS_CONTAS && s.conta !== conta) return false
    if (!termo) return true
    return texto(s.titulo, s.inv, s.sku, s.anuncios.join(' ')).includes(termo)
  })
}

export function filtrarPerdas(perdas: PerdaFull[], conta: string, busca: string): PerdaFull[] {
  const termo = busca.trim().toLowerCase()
  return perdas.filter((p) => {
    if (conta !== TODAS_AS_CONTAS && p.conta !== conta) return false
    if (!termo) return true
    return texto(p.titulo, p.inv).includes(termo)
  })
}

// ---------------------------------------------------------------- listas

/** Repor agora: o mais urgente primeiro. Sem giro (cobertura nula) nunca entra aqui. */
export function listaRepor(skus: SkuFull[]): SkuFull[] {
  return skus
    .filter((s) => SITUACOES_REPOR.includes(s.situacao))
    .sort((a, b) => (a.cobertura ?? Number.POSITIVE_INFINITY) - (b.cobertura ?? Number.POSITIVE_INFINITY))
}

/** Encalhado: o que custa mais primeiro. SKU sem custo cadastrado cai para o fim, pelo volume. */
export function listaParado(skus: SkuFull[]): SkuFull[] {
  return skus
    .filter((s) => SITUACOES_PARADO.includes(s.situacao))
    .sort((a, b) => (b.valor_parado ?? 0) - (a.valor_parado ?? 0) || b.total - a.total)
}

export function listaElegiveis(candidatos: CandidatoFull[]): CandidatoFull[] {
  return candidatos.filter((c) => c.elegivel)
}

/** "Quase lá": vende o bastante, mas não converte. Tráfego não falta — o problema é outro. */
export function listaQuaseLa(candidatos: CandidatoFull[], corteUnidades: number): CandidatoFull[] {
  return candidatos.filter((c) => !c.elegivel && c.un30 > corteUnidades)
}

// ---------------------------------------------------------------- resumo

/**
 * Recalcula o resumo para o filtro que está na tela. O motor já entrega `resumo_geral` e
 * `resumo_conta`, mas nenhum dos dois cobre "as duas contas + busca por 'copo'".
 *
 * MC% é sempre Σ margem ÷ Σ receita — ponderada pela receita, nunca média de percentuais.
 * SKU cuja margem está no nível do anúncio (variações) entra com receita e MC nulas: não soma
 * nem atrapalha, e o total de quantos são aparece na tela.
 */
export function resumir(skus: SkuFull[]): ResumoFull {
  const r: ResumoFull = {
    skus: skus.length,
    unidades_cd: 0,
    disp: 0,
    transito: 0,
    perdido: 0,
    un30: 0,
    un30_fora_cd: 0,
    urgencia: {
      esta_semana: { skus: 0, un: 0 },
      proxima_semana: { skus: 0, un: 0 },
      duas_semanas: { skus: 0, un: 0 },
      programar: { skus: 0, un: 0 },
    },
    receita: 0,
    mc: 0,
    mc_pct: null,
    receita_aj: 0,
    mc_aj: 0,
    mc_aj_pct: null,
    ajuste_pis: 0,
    ajuste_inc: 0,
    valor_parado: 0,
    ruptura: 0,
    critico: 0,
    repor: 0,
    ok: 0,
    excesso: 0,
    encalhado: 0,
    zerado_sem_giro: 0,
  }
  for (const s of skus) {
    r.unidades_cd += s.total
    r.disp += s.disp
    r.transito += s.transito
    r.perdido += s.perdido
    r.un30 += s.un30
    r.un30_fora_cd += s.un30_fora_cd ?? 0
    if (s.urgencia && s.urgencia !== 'nenhuma') {
      r.urgencia[s.urgencia].skus += 1
      r.urgencia[s.urgencia].un += s.repor
    }
    r.receita += s.receita ?? 0
    r.mc += s.mc ?? 0
    // a ajustada só soma onde existe (anúncios cobertos pela coleção margens_ajustadas): receita própria
    if (s.mc_aj != null) {
      r.receita_aj += s.receita ?? 0
      r.mc_aj += s.mc_aj
      r.ajuste_pis += s.ajuste_pis ?? 0
      r.ajuste_inc += s.ajuste_inc ?? 0
    }
    r[s.situacao] += 1
    if (SITUACOES_PARADO.includes(s.situacao)) r.valor_parado += s.valor_parado ?? 0
  }
  r.mc_pct = r.receita ? Math.round((1000 * r.mc) / r.receita) / 10 : null
  r.mc_aj_pct = r.receita_aj ? Math.round((1000 * r.mc_aj) / r.receita_aj) / 10 : null
  return r
}

/** Unidades a enviar para fechar o alvo de cobertura, somando só quem precisa repor. */
export function unidadesAEnviar(skus: SkuFull[]): number {
  return listaRepor(skus).reduce((acc, s) => acc + s.repor, 0)
}

export function unidadesParadas(skus: SkuFull[]): number {
  return skus
    .filter((s) => SITUACOES_PARADO.includes(s.situacao))
    .reduce((acc, s) => acc + s.total, 0)
}

// ---------------------------------------------------------------- textos

/**
 * A sugestão que aparece na coluna "Ação sugerida" do encalhado. É SUGESTÃO, nunca decisão:
 * o painel não manda retirar nada do Full sozinho.
 */
export function acaoSugerida(s: SkuFull): string {
  if (s.situacao === 'excesso') {
    const dias = s.cobertura == null ? null : Math.round(s.cobertura)
    return dias == null
      ? 'Parar de enviar — já há cobertura de sobra no CD.'
      : `Parar de enviar. Cobertura de ${dias} dias já no CD.`
  }
  if (s.visitas30 >= 100) {
    return 'Tem tráfego e não converte — testar preço ou anúncio antes de retirar.'
  }
  if (s.visitas30 > 0) {
    return 'Pouca visita e zero venda — retirar do Full ou dar saída em promoção.'
  }
  return 'Sem visita e sem venda — anúncio provavelmente sem exposição. Retirar do Full.'
}

/** O que checar antes de mandar um anúncio de coleta para o Full. */
export function ressalvasMigracao(c: CandidatoFull): string[] {
  const out: string[] = []
  if (c.status !== 'active') out.push(`Anúncio está ${c.status} — reativar primeiro.`)
  if (c.mc_pct != null && c.mc_pct < 0) {
    out.push('MC negativa — enviar pro Full aumenta o prejuízo por unidade.')
  }
  if (c.visitas30 < 200) out.push('Base de visitas pequena — conversão pouco confiável.')
  if (c.mc_pct == null) out.push('Sem MC no período — conferir custo antes.')
  return out
}

/** Um SKU pode ter anúncio direto e variação; a tela mostra os dois juntos. */
export function mlbsDoSku(s: SkuFull): string[] {
  const das = s.variacoes.map((v) => v.split(':')[0])
  return Array.from(new Set([...s.anuncios, ...das]))
}

export function coresDoSku(s: SkuFull): string[] {
  return s.variacoes.map((v) => v.split(':')[1]).filter((c): c is string => Boolean(c))
}

export function linkAnuncio(mlb: string): string {
  return `https://produto.mercadolivre.com.br/MLB-${mlb.replace('MLB', '')}`
}

export function apelidoConta(dados: EstoqueFullData, conta: string): string {
  return dados.contas[conta]?.apelido ?? conta
}

// ---------------------------------------------------------------- formatação

const BRL0 = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
})
const BRL2 = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const NUM = new Intl.NumberFormat('pt-BR')

/** Nada de "R$ 0" quando o número não existe: sem dado, a tela escreve "—". */
export function brl(v: number | null | undefined, casas: 0 | 2 = 0): string {
  if (v == null) return '—'
  return casas === 2 ? BRL2.format(v) : BRL0.format(v)
}

export function num(v: number | null | undefined): string {
  if (v == null) return '—'
  return NUM.format(v)
}

export function pct(v: number | null | undefined): string {
  if (v == null) return '—'
  return `${v.toString().replace('.', ',')}%`
}

export function dias(v: number | null | undefined): string {
  if (v == null) return 'sem giro'
  return `${NUM.format(Math.round(v))}d`
}
